import { extendTheme } from '@chakra-ui/react'
import { withFlagFont } from './flagFont'

/**
 * Runtime branding: turn a single tenant primary colour (from the Studio client
 * API `meta.branding.primary_color`) into a full Chakra palette and theme — so a
 * new client needs NO per-client theme file. Branding becomes data, not code.
 */

function clamp(n: number): number {
	return Math.max(0, Math.min(255, Math.round(n)))
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
	let h = hex.trim().replace(/^#/, '')
	if (h.length === 3)
		h = h
			.split('')
			.map(c => c + c)
			.join('')
	if (!/^[0-9a-fA-F]{6}$/.test(h)) return null
	return {
		r: parseInt(h.slice(0, 2), 16),
		g: parseInt(h.slice(2, 4), 16),
		b: parseInt(h.slice(4, 6), 16),
	}
}

function toHex(r: number, g: number, b: number): string {
	const h = (n: number) => clamp(n).toString(16).padStart(2, '0')
	return `#${h(r)}${h(g)}${h(b)}`
}

// Mix toward white (amount>0) or black (amount<0). amount in [-1, 1].
function mix(rgb: { r: number; g: number; b: number }, amount: number): string {
	const target = amount >= 0 ? 255 : 0
	const t = Math.abs(amount)
	return toHex(
		rgb.r + (target - rgb.r) * t,
		rgb.g + (target - rgb.g) * t,
		rgb.b + (target - rgb.b) * t
	)
}

/** Build a Chakra 50–900 scale around a base colour (base = 500). */
export function paletteFromHex(hex: string): Record<number, string> | null {
	const rgb = hexToRgb(hex)
	if (!rgb) return null
	return {
		50: mix(rgb, 0.9),
		100: mix(rgb, 0.78),
		200: mix(rgb, 0.6),
		300: mix(rgb, 0.4),
		400: mix(rgb, 0.2),
		500: toHex(rgb.r, rgb.g, rgb.b),
		600: mix(rgb, -0.15),
		700: mix(rgb, -0.3),
		800: mix(rgb, -0.45),
		900: mix(rgb, -0.6),
	}
}

/** Radius-preset → RadiusTokens lookup for `radius_scale` (see lib/theme.ts). */
const RADIUS_PRESETS: Record<
	string,
	{ sm: string; md: string; lg: string; xl: string }
> = {
	sharp: { sm: '0', md: '0', lg: '0', xl: '0' },
	rounded: { sm: '0.25rem', md: '0.5rem', lg: '0.75rem', xl: '1rem' },
	pill: { sm: '9999px', md: '9999px', lg: '9999px', xl: '9999px' },
}

export interface BrandThemeTokens {
	primaryColor?: string
	secondaryColor?: string
	accentColor?: string
	successColor?: string
	warningColor?: string
	destructiveColor?: string
	fontHeading?: string
	fontBody?: string
	radiusScale?: string
	/**
	 * Raw scale overrides from the tenant's BYOD design system, keyed the same
	 * way Chakra keys them (`sm`/`md`/`lg`/…). ONLY the tenant's explicit
	 * overrides belong here — a resolved scale would replace the bundle
	 * theme's whole scale with platform defaults.
	 *
	 * These retrofit existing markup because Chakra components already
	 * reference these scales by name (`borderRadius="md"`, `shadow="md"`,
	 * `fontSize="lg"`). Spacing is deliberately absent: Chakra's space scale
	 * is numeric (`p={4}`), so named spacing tokens would not reach existing
	 * components — they stay available as CSS variables instead.
	 */
	radii?: Record<string, string>
	shadows?: Record<string, string>
	fontSizes?: Record<string, string>
}

/**
 * Return a theme with the tenant's brand tokens applied over `baseTheme`.
 * Each token is independently optional — an unconfigured field leaves
 * `baseTheme`'s existing value untouched, so a tenant with no branding (or
 * only some fields set) keeps the env-selected theme (no regression).
 */
export function buildBrandTheme(
	baseTheme: any,
	tokens?: BrandThemeTokens
): any {
	if (!tokens) return baseTheme

	const primaryPalette = tokens.primaryColor
		? paletteFromHex(tokens.primaryColor)
		: null
	const secondaryPalette = tokens.secondaryColor
		? paletteFromHex(tokens.secondaryColor)
		: null
	const accentPalette = tokens.accentColor
		? paletteFromHex(tokens.accentColor)
		: null
	const successPalette = tokens.successColor
		? paletteFromHex(tokens.successColor)
		: null
	const warningPalette = tokens.warningColor
		? paletteFromHex(tokens.warningColor)
		: null
	const destructivePalette = tokens.destructiveColor
		? paletteFromHex(tokens.destructiveColor)
		: null
	const radii = tokens.radiusScale ? RADIUS_PRESETS[tokens.radiusScale] : null
	// Explicit BYOD scale overrides. A preset radiusScale still applies first,
	// with individual radius tokens layered over it, so the two can coexist.
	const has = (o?: Record<string, string>) =>
		Boolean(o && Object.keys(o).length)

	if (
		!primaryPalette &&
		!secondaryPalette &&
		!accentPalette &&
		!successPalette &&
		!warningPalette &&
		!destructivePalette &&
		!tokens.fontHeading &&
		!tokens.fontBody &&
		!radii &&
		!has(tokens.radii) &&
		!has(tokens.shadows) &&
		!has(tokens.fontSizes)
	) {
		return baseTheme
	}

	return extendTheme({
		...baseTheme,
		colors: {
			...baseTheme.colors,
			...(primaryPalette ? { primary: primaryPalette } : {}),
			...(secondaryPalette ? { secondary: secondaryPalette } : {}),
			...(accentPalette ? { accent: accentPalette } : {}),
			...(successPalette ? { success: successPalette } : {}),
			...(warningPalette ? { warning: warningPalette } : {}),
			...(destructivePalette ? { destructive: destructivePalette } : {}),
		},
		fonts: {
			...baseTheme.fonts,
			...(tokens.fontHeading
				? { heading: withFlagFont(tokens.fontHeading) }
				: {}),
			...(tokens.fontBody ? { body: withFlagFont(tokens.fontBody) } : {}),
		},
		radii: {
			...(baseTheme.radii ?? {}),
			...(radii ?? {}),
			...(tokens.radii ?? {}),
		},
		shadows: {
			...(baseTheme.shadows ?? {}),
			...(tokens.shadows ?? {}),
		},
		fontSizes: {
			...(baseTheme.fontSizes ?? {}),
			...(tokens.fontSizes ?? {}),
		},
	})
}
